import React, { useState } from 'react';
import { Card, Typography, Button, Tooltip } from 'antd';
import {
    ThunderboltOutlined,
    BranchesOutlined,
    ApartmentOutlined,
    MailOutlined,
    ApiOutlined,
    EditOutlined,
    CheckCircleOutlined,
    StopOutlined,
    DownOutlined,
    LeftOutlined,
    RightOutlined,
    AppstoreOutlined
} from '@ant-design/icons';

const { Text } = Typography;

const NodePalette = ({ position = 'left', onAddNode }) => {
    // State for entire palette collapse.
    const [collapsed, setCollapsed] = useState(false);
    // State for each category's expanded/collapsed status.
    const [expanded, setExpanded] = useState({
        triggers: true,
        control: true,
        actions: true,
        termination: true,
    });

    const toggleSection = (section) => {
        setExpanded((prev) => ({
            ...prev,
            [section]: !prev[section],
        }));
    };

    // Category component with smooth animation.
    const NodeCategory = ({ title, nodes, sectionKey }) => {
        const isExpanded = expanded[sectionKey];
        return (
            <div style={{ marginBottom: 16 }}>
                <div
                    onClick={() => toggleSection(sectionKey)}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        padding: '6px 8px',
                        userSelect: 'none',
                        background: '#fff',
                        borderRadius: 4,
                    }}
                >
                    <DownOutlined
                        style={{
                            marginRight: 8,
                            fontSize: 10,
                            transition: 'transform 0.3s ease',
                            transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                            color: 'rgb(32,126,220)',
                        }}
                    />
                    <Text strong style={{ color: 'rgb(32,126,220)' }}>
                        {title}
                    </Text>
                </div>
                <div
                    style={{
                        marginTop: 8,
                        transition: 'max-height 0.3s ease, opacity 0.3s ease',
                        maxHeight: isExpanded ? '500px' : '0px',
                        opacity: isExpanded ? 1 : 0,
                        overflow: 'hidden',
                    }}
                >
                    {nodes.map((node) => (
                        <Card
                            key={node.type}
                            size="small"
                            style={{
                                cursor: 'pointer',
                                borderRadius: 4,
                                transition: 'all 0.3s',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                marginBottom: 8,
                            }}
                            hoverable
                            onClick={() => onAddNode(node.type, title)}
                            bodyStyle={{
                                padding: '8px 12px',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            {node.icon}
                            <Text style={{ marginLeft: 8 }}>{node.label}</Text>
                        </Card>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <>
            {collapsed ? (
                // Collapsed palette: a small vertical tab with the 4-box icon.
                <div
                    style={{
                        position: 'absolute',
                        [position]: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        background: 'rgba(255,255,255,0.95)',
                        padding: 8,
                        borderRadius: position === 'left' ? '0 4px 4px 0' : '4px 0 0 4px',
                        cursor: 'pointer',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease',
                    }}
                    onClick={() => setCollapsed(false)}
                >
                    <Tooltip title="Expand Palette">
                        <AppstoreOutlined style={{ fontSize: 20, color: 'rgb(32,126,220)' }} />
                    </Tooltip>
                </div>
            ) : (
                // Expanded palette.
                <div
                    style={{
                        position: 'absolute',
                        [position]: 10,
                        top: 10,
                        zIndex: 10,
                        width: 240,
                        transition: 'transform 0.3s ease, opacity 0.3s ease',
                        transform: collapsed ? 'translateX(-120%)' : 'translateX(0)',
                    }}
                >
                    <Card
                        style={{
                            background: 'rgba(255,255,255,0.95)',
                            border: 'none',
                            borderRadius: 8,
                        }}
                        bodyStyle={{
                            padding: '12px',
                            maxHeight: 'calc(100vh - 180px)',
                            overflowY: 'auto',
                        }}
                        size="small"
                        title={
                            <div
                                onClick={() => setCollapsed(true)}
                                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                            >
                                <AppstoreOutlined style={{ fontSize: 20, color: 'rgb(32,126,220)', marginRight: 8 }} />
                                <Text strong style={{ color: 'rgb(32,126,220)' }}>
                                    Components
                                </Text>
                            </div>
                        }
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <NodeCategory
                                title="Triggers"
                                sectionKey="triggers"
                                nodes={[
                                    {
                                        type: 'trigger',
                                        label: 'New Call Evaluation',
                                        icon: <ThunderboltOutlined style={{ color: '#faad14' }} />,
                                    },
                                ]}
                            />
                            <NodeCategory
                                title="Control"
                                sectionKey="control"
                                nodes={[
                                    {
                                        type: 'conditional',
                                        label: 'Conditional (If/Else)',
                                        icon: <BranchesOutlined style={{ color: '#1890ff' }} />,
                                    },
                                    {
                                        type: 'switch',
                                        label: 'Switch',
                                        icon: <ApartmentOutlined style={{ color: '#722ed1' }} />,
                                    },
                                ]}
                            />
                            <NodeCategory
                                title="Actions"
                                sectionKey="actions"
                                nodes={[
                                    {
                                        type: 'email',
                                        label: 'Send Email',
                                        icon: <MailOutlined style={{ color: '#1890ff' }} />,
                                    },
                                    {
                                        type: 'webhook',
                                        label: 'Web Hook',
                                        icon: <ApiOutlined style={{ color: '#f759ab' }} />,
                                    },
                                    {
                                        type: 'updateRecord',
                                        label: 'Update Record',
                                        icon: <EditOutlined style={{ color: '#52c41a' }} />,
                                    },
                                ]}
                            />
                            <NodeCategory
                                title="Termination"
                                sectionKey="termination"
                                nodes={[
                                    {
                                        type: 'complete',
                                        label: 'Complete',
                                        icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                                    },
                                    {
                                        type: 'error',
                                        label: 'Error',
                                        icon: <StopOutlined style={{ color: '#f5222d' }} />,
                                    },
                                ]}
                            />
                        </div>
                    </Card>
                </div>
            )}
        </>
    );
};

export default NodePalette;
